/**
 * Generic byte <-> base64 / hex encoders shared by the camera encryption
 * modules. These are pure, domain-agnostic helpers (no crypto, no key logic).
 */

/**
 * Decodes a base64 string into its raw bytes.
 *
 * @param value - The base64-encoded input.
 * @returns The decoded bytes.
 */
export const base64ToBytes = (value: string): Uint8Array => {
	const binary = atob(value);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i += 1) {
		bytes[i] = binary.charCodeAt(i);
	}
	return bytes;
};

/**
 * Encodes raw bytes as a base64 string.
 *
 * @param bytes - The bytes to encode.
 * @returns The base64-encoded string.
 */
export const bytesToBase64 = (bytes: Uint8Array): string => {
	let binary = '';
	for (let i = 0; i < bytes.length; i += 1) {
		binary += String.fromCharCode(bytes[i]);
	}
	return btoa(binary);
};

/**
 * Encodes raw bytes as a lowercase hex string.
 *
 * @param bytes - The bytes to encode.
 * @returns The hex-encoded string.
 */
export const bytesToHex = (bytes: Uint8Array): string => {
	return Array.from(bytes)
		.map((b) => b.toString(16).padStart(2, '0'))
		.join('');
};

/**
 * Decodes a lowercase hex string into its raw bytes.
 *
 * @param hex - The hex-encoded input (even length).
 * @returns The decoded bytes, or `null` when the input is not valid hex.
 */
export const hexToBytes = (hex: string): Uint8Array | null => {
	if (!hex || hex.length % 2 !== 0 || /[^0-9a-fA-F]/.test(hex)) return null;
	const bytes = new Uint8Array(hex.length / 2);
	for (let i = 0; i < bytes.length; i += 1) {
		bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
	}
	return bytes;
};
